import { FunctionComponent, lazy, Suspense, useCallback } from 'react'
import { Redirect, Route, Switch, useHistory } from 'react-router-dom'

import {
  ChangeOrgForm,
  CreatePasswordForm,
  ForgotPasswordForm,
  ForgotUsernameForm,
  LoginForm,
  ResendInviteForm,
  VerifyForm,
} from '@medable/medable-web-framework'
import { useI18nTranslation } from '@medable/i18n-core'

import { Loading } from '@/components/loading'
import { NotificationService } from '@/services/notification'
import { Routes } from './router.constants'
import { PrivateRoute } from './private-route.component'

const DashboardPage = lazy(() => import('@/pages/dashboard'))

export const Router: FunctionComponent = () => {
  const history = useHistory()
  const { t } = useI18nTranslation()

  const {
    location: { search },
  } = history

  const goToLogin = useCallback(() => {
    history.push(Routes.LOGIN)
  }, [history])

  const goToDashboard = useCallback(() => {
    history.push(Routes.DASHBOARD)
  }, [history])

  const afterCreatePassword = useCallback(() => {
    goToLogin()

    NotificationService.notify({
      title: t('create-password.success-title'),
      message: t('create-password.success-message'),
    })
  }, [goToLogin, t])

  const afterResetPassword = useCallback(() => {
    goToLogin()

    NotificationService.notify({
      title: t('reset-password.success-title'),
      message: t('reset-password.success-message'),
    })
  }, [goToLogin, t])

  const afterExpiredPassword = useCallback(() => {
    goToDashboard()
  }, [goToDashboard])

  return (
    <Suspense fallback={<Loading />}>
      <Switch>
        <Route exact path={Routes.DASHBOARD} key={Routes.DASHBOARD}>
          <PrivateRoute Component={DashboardPage} />
        </Route>

        <Route exact path={Routes.LOGIN} key={Routes.LOGIN}>
          <LoginForm afterLogin={goToDashboard} isSSOEnabled />
        </Route>

        <Route exact path={Routes.VERIFY} key={Routes.VERIFY}>
          <VerifyForm afterVerify={goToLogin} />
        </Route>

        <Route exact path={Routes.RESET_USERNAME} key={Routes.RESET_USERNAME}>
          <ForgotUsernameForm afterSubmit={goToLogin} />
        </Route>

        <Route exact path={Routes.CREATE_PASSWORD} key={Routes.CREATE_PASSWORD}>
          <CreatePasswordForm afterSubmit={afterExpiredPassword} />
        </Route>

        <Route exact path={Routes.CREATE_PASSWORD_WITH_TOKEN} key={Routes.CREATE_PASSWORD_WITH_TOKEN}>
          <CreatePasswordForm afterSubmit={afterCreatePassword} />
        </Route>

        <Route exact path={Routes.RESET_PASSWORD_WITH_TOKEN} key={Routes.RESET_PASSWORD_WITH_TOKEN}>
          <CreatePasswordForm afterSubmit={afterResetPassword} />
        </Route>

        <Route exact path={Routes.RESET_PASSWORD} key={Routes.RESET_PASSWORD}>
          <ForgotPasswordForm afterSubmit={goToLogin} />
        </Route>

        <Route exact path={Routes.RESEND_PIN} key={Routes.RESEND_PIN}>
          <ResendInviteForm afterSubmit={goToLogin} />
        </Route>

        <Route exact path={Routes.ORG_SELECT} key={Routes.ORG_SELECT}>
          <ChangeOrgForm />
        </Route>

        <Route path={Routes.ROOT} key={Routes.ROOT}>
          <Redirect to={{ pathname: Routes.DASHBOARD, search, state: { from: Routes.DASHBOARD } }} />
        </Route>
      </Switch>
    </Suspense>
  )
}
